import { Injectable } from '@angular/core'
import { Auth } from '@angular/fire/auth'
import { addDoc, collection, collectionData, deleteDoc, doc, Firestore, orderBy, query, serverTimestamp, updateDoc } from '@angular/fire/firestore'
import { Observable } from 'rxjs'
import { AnnotationWithId, FreshAnnotation, BiopsyAnnotation, CustomAnnotation, BodyPartAnnotation, Fresh } from '@innersight-os/shared/firestore-interfaces'
import { DocumentData } from '@firebase/firestore-types'

type CommonFields = 'createdAt' | 'createdBy' | 'operationId' | 'uploadId' | 'volumeId' | 'type'
export type BiopsyAnnotationUpdate   = Partial<Omit<BiopsyAnnotation,   CommonFields>>
export type BodyPartAnnotationUpdate = Partial<Omit<BodyPartAnnotation, CommonFields>>
export type CustomAnnotationUpdate   = Partial<Omit<CustomAnnotation,   CommonFields>>

@Injectable({
  providedIn: 'root'
})
export class AnnotationsService {
  constructor(
    private auth: Auth,
    private db: Firestore,
  ) {}

  getAnnotations = (operationId: string, uploadId: string, volumeId: string): Observable<AnnotationWithId[]> =>
    collectionData(
      query<AnnotationWithId, DocumentData>(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collection(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/annotations`) as any,
        orderBy('createdAt', 'desc'),
      ),
      {idField: 'id'},
    ) as Observable<AnnotationWithId[]>

  async createAnnotation(annotation: Fresh<Omit<BiopsyAnnotation, 'createdAt' | 'createdBy'> | Omit<BodyPartAnnotation, 'createdAt' | 'createdBy'> | Omit<CustomAnnotation, 'createdAt' | 'createdBy'>>) {
    const freshAnnotation: FreshAnnotation = {
      ...annotation,
      ...this.createdFields(),
    }

    return addDoc(
      collection(this.db, `operations/${annotation.operationId}/uploads/${annotation.uploadId}/volumes/${annotation.volumeId}/annotations`),
      freshAnnotation,
    )
  }

  updateBiopsyAnnotation(operationId: string, uploadId: string, volumeId: string, id: string, update: BiopsyAnnotationUpdate) {
    return updateDoc(
      doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/annotations/${id}`),
      update,
    )
  }

  updateBodyPartAnnotation(operationId: string, uploadId: string, volumeId: string, id: string, update: BodyPartAnnotationUpdate) {
    return updateDoc(
      doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/annotations/${id}`),
      update,
    )
  }

  updateCustomAnnotation(operationId: string, uploadId: string, volumeId: string, id: string, update: CustomAnnotationUpdate) {
    return updateDoc(
      doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/annotations/${id}`),
      update,
    )
  }

  delete(annotation: AnnotationWithId) {
    return deleteDoc(this.getDocFromData(annotation))
  }

  private createdFields = () => ({
    createdAt: serverTimestamp(),
    createdBy: this.auth.currentUser.uid,
  })

  private getDocFromData({operationId, uploadId, volumeId, id}: AnnotationWithId) {
    return doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/annotations/${id}`)
  }
}
